import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { QuestionRendererProps } from './types';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle, Search, ArrowRight, X, AlertTriangle, Sparkles } from 'lucide-react';
import { hexToRgba, FALLBACK_COLOR } from './rendererUtils';
import { useEdgeTriggeredSubmit } from './useEdgeTriggeredSubmit';
type ErrorDef = {
  word: string;
  position: number;
  correction: string;
  rule?: string;
};
const MATH_TOKEN_RE = /[+\-−×÷=*/]/;
/** A token in the rendered text. */
type Token = {
  index: number; // position in original split
  raw: string; // raw word
  clean: string; // word with trailing punctuation stripped
  punctuation: string; // stripped trailing punctuation
  isTappable: boolean; // false for label/punctuation/skipWords
  isMathy: boolean; // contains +, -, ×, ÷, =, etc.
};
function isPurePunctuation(s: string): boolean {
  return /^[.,!?;:()"'…—–-]+$/.test(s);
}
function isLabelWord(s: string): boolean {
  // Ends with ":" and starts with a letter (e.g., "Yara:", "Antwoord:")
  return /^[A-Za-zÀ-ÿ][A-Za-zÀ-ÿ\s]*:$/.test(s);
}
export function ErrorSpottingRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate,
  moduleColor
}: QuestionRendererProps) {
  const color = moduleColor || FALLBACK_COLOR;
  const config = question.data || {};
  const text: string = config.text || '';
  const errors: ErrorDef[] = config.errors || [];
  const skipWordsFromSeed: number[] = Array.isArray(config.skipWords) ? config.skipWords : [];
  const [corrections, setCorrections] = useState<Record<number, string>>({});
  const [confirmedNoErrors, setConfirmedNoErrors] = useState(false);
  const zeroErrorMode = errors.length === 0;
  // Tokenize text into tappable / non-tappable units
  const tokens = useMemo<Token[]>(() => {
    const rawWords = text.split(/\s+/).filter(w => w.length > 0);
    return rawWords.map((raw, index) => {
      const match = raw.match(/^(.*?)([.,!?;:]*)$/);
      const clean = match ? match[1] : raw;
      const punctuation = match ? match[2] : '';
      const skipExplicit = skipWordsFromSeed.includes(index);
      const skipAuto = clean === '' || isPurePunctuation(raw) || isLabelWord(raw);
      const isTappable = !skipExplicit && !skipAuto;
      const isMathy = MATH_TOKEN_RE.test(clean);
      return {
        index,
        raw,
        clean,
        punctuation,
        isTappable,
        isMathy
      };
    });
  }, [text, skipWordsFromSeed]);
  // Set of valid error positions for quick lookup
  const errorByPosition = useMemo(() => {
    const map = new Map<number, ErrorDef>();
    errors.forEach(e => map.set(e.position, e));
    return map;
  }, [errors]);
  useEffect(() => {
    setCorrections({});
    setConfirmedNoErrors(false);
  }, [question.id]);
  // Ready logic:
  // - Zero-error mode: ready when child confirms "geen fouten"
  // - Normal mode: ready when marked count >= 1 AND all marks have non-empty correction
  useEffect(() => {
    if (zeroErrorMode) {
      onReadyChange?.(confirmedNoErrors);
      return;
    }
    const keys = Object.keys(corrections).map(Number);
    const hasMarks = keys.length > 0;
    const allFilled = keys.every(pos => corrections[pos]?.trim() !== '');
    onReadyChange?.(hasMarks && allFilled);
  }, [corrections, confirmedNoErrors, zeroErrorMode, onReadyChange]);
  const answerPayload = useMemo(() => JSON.stringify(corrections), [corrections]);
  const handleEdgeSubmit = useCallback((payload: string) => {
    onAnswer(payload);
  }, [onAnswer]);
  useEdgeTriggeredSubmit(answerPayload, shouldValidate, handleEdgeSubmit, question.id);
  const handleWordClick = (token: Token) => {
    if (shouldValidate) return;
    if (!token.isTappable) return;
    if (zeroErrorMode && confirmedNoErrors) setConfirmedNoErrors(false);
    setCorrections(prev => {
      const next = {
        ...prev
      };
      if (next[token.index] !== undefined) {
        delete next[token.index];
      } else {
        next[token.index] = '';
      }
      return next;
    });
  };
  const handleRemoveCorrection = (index: number) => {
    if (shouldValidate) return;
    setCorrections(prev => {
      const next = {
        ...prev
      };
      delete next[index];
      return next;
    });
  };
  const handleCorrectionChange = (index: number, value: string) => {
    if (shouldValidate) return;
    setCorrections(prev => ({
      ...prev,
      [index]: value
    }));
  };
  if (!text) {
    return <div className="text-center p-8 bg-red-50 rounded-xl border-2 border-red-200" data-id="element-10402">
        <p className="text-red-700 font-bold" data-id="element-10403">
          ⚠️ Configuratie ontbreekt (text)
        </p>
      </div>;
  }
  const markedIndices = Object.keys(corrections).map(Number).sort((a, b) => a - b);
  const foundCount = markedIndices.length;
  // Post-submit: classify marks
  const wronglyMarked: number[] = shouldValidate ? markedIndices.filter(i => !errorByPosition.has(i)) : [];
  const missedErrors: ErrorDef[] = shouldValidate ? errors.filter(e => corrections[e.position] === undefined) : [];
  return <div className="w-full max-w-3xl mx-auto flex flex-col gap-6" data-id="element-10404">
      {/* Status Header */}
      <div className="flex items-center justify-between rounded-2xl p-4 border-2 gap-3 flex-wrap" style={{
      backgroundColor: hexToRgba(color, 0.05),
      borderColor: hexToRgba(color, 0.15)
    }} data-id="element-10405">
        <div className="flex items-center gap-3 font-bold text-sm sm:text-base" style={{
        color
      }} data-id="element-10406">
          <Search className="w-5 h-5 shrink-0" data-id="element-10407" />
          <span data-id="element-10408">
            {zeroErrorMode ? 'Lees de tekst goed. Zit er een fout in?' : 'Tik op fout woorden en typ de juiste schrijfwijze'}
          </span>
        </div>
        {!zeroErrorMode && <div className="flex items-center gap-2" data-id="element-10409">
            <span className="text-xs sm:text-sm font-medium" style={{
          color: hexToRgba(color, 0.8)
        }} data-id="element-10410">
              Gevonden:
            </span>
            <div className="bg-white px-3 py-1 rounded-lg font-bold shadow-sm border text-sm" style={{
          color,
          borderColor: hexToRgba(color, 0.15)
        }} data-id="element-10411">
              {foundCount}
            </div>
          </div>}
      </div>

      {/* Text Area */}
      <div className="bg-white rounded-3xl p-6 sm:p-8 md:p-10 border-2 border-slate-200 shadow-sm leading-loose text-lg sm:text-xl" data-id="element-10412">
        <div className="flex flex-wrap gap-x-1.5 gap-y-3" data-id="element-10413">
          {tokens.map(token => {
          const isMarked = corrections[token.index] !== undefined;
          const isPostSubmitWrong = shouldValidate && isMarked && !errorByPosition.has(token.index);
          const isPostSubmitMissed = shouldValidate && !isMarked && errorByPosition.has(token.index);
          let btnClass = 'relative px-2 py-1 rounded-lg transition-all font-medium ';
          const btnStyle: React.CSSProperties = {};
          if (!token.isTappable) {
            btnClass += 'cursor-default text-slate-500 ';
          } else if (shouldValidate) {
            btnClass += 'cursor-default ';
            if (isPostSubmitWrong) {
              btnClass += 'bg-amber-50 text-amber-700 border border-amber-200 line-through ';
            } else if (isMarked) {
              btnClass += 'bg-slate-100 text-slate-500 line-through ';
            } else if (isPostSubmitMissed) {
              btnClass += 'bg-red-50 text-red-700 border-2 border-red-300 border-dashed ';
            } else {
              btnClass += 'text-slate-800 ';
            }
          } else {
            btnClass += 'cursor-pointer ';
            if (isMarked) {
              btnClass += 'bg-red-50 text-red-700 line-through border border-red-200 shadow-sm ';
            } else {
              btnClass += 'text-slate-800 hover:bg-slate-100 ';
            }
          }
          return <span key={token.index} className="inline-flex items-baseline" data-id="element-10414">
                <button type="button" onClick={() => handleWordClick(token)} disabled={shouldValidate || !token.isTappable} className={btnClass} style={btnStyle} aria-label={token.isTappable ? `Woord ${token.clean}` : `Niet-aanklikbaar: ${token.clean}`} data-id="element-10415">
                  {token.clean || token.raw}
                </button>
                {token.punctuation && <span className="text-slate-800 self-end pb-1 ml-0.5" data-id="element-10416">
                    {token.punctuation}
                  </span>}
              </span>;
        })}
        </div>
      </div>

      {/* Zero-error mode: confirm button */}
      {zeroErrorMode && !shouldValidate && <motion.button type="button" onClick={() => setConfirmedNoErrors(v => !v)} whileTap={{
      scale: 0.98
    }} className={`w-full flex items-center justify-center gap-3 rounded-2xl p-4 border-2 font-bold transition-all ${confirmedNoErrors ? 'bg-emerald-50 border-emerald-300 text-emerald-700 shadow-sm' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`} data-id="element-10417">
          {confirmedNoErrors ? <>
              <CheckCircle2 className="w-5 h-5" data-id="element-10418" />
              Geen fouten gevonden — klaar om in te leveren
            </> : <>
              <Sparkles className="w-5 h-5" data-id="element-10419" />
              Geen fouten? Tik hier om te bevestigen
            </>}
        </motion.button>}

      {/* Corrections List */}
      <AnimatePresence data-id="element-10420">
        {markedIndices.length > 0 && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="flex flex-col gap-3" data-id="element-10421">
            <h4 className="text-sm font-bold text-slate-500 uppercase tracking-wider ml-2" data-id="element-10422">
              Jouw verbeteringen
            </h4>
            <div className="flex flex-col gap-3" data-id="element-10423">
              <AnimatePresence mode="popLayout" data-id="element-10424">
                {markedIndices.map(index => {
              const token = tokens[index];
              const originalWord = token?.clean || tokens[index]?.raw || '';
              const userCorrection = corrections[index];
              const errorDef = errorByPosition.get(index);
              let isCorrect = false;
              let showValidation = false;
              let isWronglyMarked = false;
              if (shouldValidate) {
                showValidation = true;
                if (errorDef) {
                  isCorrect = userCorrection.toLowerCase().trim() === errorDef.correction.toLowerCase().trim();
                } else {
                  isWronglyMarked = true;
                }
              }
              const isMathy = token?.isMathy;
              return <motion.div layout initial={{
                opacity: 0,
                scale: 0.95,
                x: -10
              }} animate={{
                opacity: 1,
                scale: 1,
                x: 0
              }} exit={{
                opacity: 0,
                scale: 0.95,
                x: 10
              }} transition={{
                duration: 0.2
              }} key={index} className={`flex items-start gap-3 sm:gap-4 p-3 sm:p-4 rounded-2xl border-2 shadow-sm bg-white ${showValidation ? isWronglyMarked ? 'border-amber-200 bg-amber-50/50' : isCorrect ? 'border-emerald-200 bg-emerald-50/50' : 'border-red-200 bg-red-50/50' : 'border-slate-200'}`} data-id="element-10425">
                      <div className="flex-1 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4" data-id="element-10426">
                        <div className="flex items-center gap-2 min-w-[120px]" data-id="element-10427">
                          <span className={`text-slate-400 line-through font-medium truncate ${isMathy ? 'font-mono' : ''}`} data-id="element-10428">
                            {originalWord}
                          </span>
                          <ArrowRight className="w-4 h-4 text-slate-300 shrink-0" data-id="element-10429" />
                        </div>

                        <div className="flex-1 relative" data-id="element-10430">
                          {isWronglyMarked ? <div className="px-4 py-2 rounded-xl bg-amber-100/50 text-amber-800 text-sm font-medium flex items-center gap-2" data-id="element-10431">
                              <AlertTriangle className="w-4 h-4 shrink-0" data-id="element-10432" />
                              Dit woord was al goed gespeld
                            </div> : <input type="text" value={userCorrection} onChange={e => handleCorrectionChange(index, e.target.value)} disabled={shouldValidate} placeholder="Typ de verbetering..." autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false} className={`w-full px-4 py-2 rounded-xl border-2 outline-none transition-all font-bold ${isMathy ? 'font-mono tracking-wide' : ''} ${showValidation ? isCorrect ? 'border-emerald-300 bg-white text-emerald-700' : 'border-red-300 bg-white text-red-700' : 'border-slate-200 focus:border-indigo-400 bg-slate-50 focus:bg-white text-slate-800'}`} autoFocus={!shouldValidate && userCorrection === ''} data-id="element-10433" />}
                        </div>
                      </div>

                      {showValidation ? <div className="shrink-0 flex items-center justify-center w-8 pt-1" data-id="element-10434">
                          {isWronglyMarked ? <AlertTriangle className="w-6 h-6 text-amber-500" data-id="element-10435" /> : isCorrect ? <CheckCircle2 className="w-6 h-6 text-emerald-500" data-id="element-10436" /> : <XCircle className="w-6 h-6 text-red-500" data-id="element-10437" />}
                        </div> : <button type="button" onClick={() => handleRemoveCorrection(index)} className="shrink-0 p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors" aria-label="Verwijder" data-id="element-10438">
                          <X className="w-5 h-5" data-id="element-10439" />
                        </button>}
                    </motion.div>;
            })}
              </AnimatePresence>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Post-submit: missed errors reveal */}
      <AnimatePresence data-id="element-10440">
        {shouldValidate && missedErrors.length > 0 && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0
      }} className="flex flex-col gap-3" data-id="element-10441">
            <h4 className="text-sm font-bold text-red-500 uppercase tracking-wider ml-2 flex items-center gap-2" data-id="element-10442">
              <AlertTriangle className="w-4 h-4" data-id="element-10443" />
              {missedErrors.length === 1 ? 'Deze fout heb je gemist' : `Deze ${missedErrors.length} fouten heb je gemist`}
            </h4>
            <div className="flex flex-col gap-2" data-id="element-10444">
              {missedErrors.map(err => <div key={err.position} className="flex items-center gap-3 p-3 rounded-2xl bg-red-50/60 border-2 border-red-200 border-dashed" data-id="element-10445">
                  <div className="flex items-center gap-2 min-w-[120px]" data-id="element-10446">
                    <span className={`text-red-500 line-through font-medium ${MATH_TOKEN_RE.test(err.word) ? 'font-mono' : ''}`} data-id="element-10447">
                      {err.word}
                    </span>
                    <ArrowRight className="w-4 h-4 text-red-300 shrink-0" data-id="element-10448" />
                    <span className={`text-emerald-700 font-bold ${MATH_TOKEN_RE.test(err.correction) ? 'font-mono' : ''}`} data-id="element-10449">
                      {err.correction}
                    </span>
                  </div>
                  {err.rule && <span className="text-xs sm:text-sm text-slate-600 flex-1" data-id="element-10450">
                      {err.rule}
                    </span>}
                </div>)}
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Post-submit: zero-error confirmation feedback */}
      <AnimatePresence data-id="element-10451">
        {shouldValidate && zeroErrorMode && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0
      }} className={`flex items-center gap-3 p-4 rounded-2xl border-2 ${foundCount === 0 ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-red-50 border-red-200 text-red-700'}`} data-id="element-10452">
            {foundCount === 0 ? <>
                <CheckCircle2 className="w-6 h-6" data-id="element-10453" />
                <span className="font-bold" data-id="element-10454">
                  Top! Deze tekst was inderdaad foutloos.
                </span>
              </> : <>
                <XCircle className="w-6 h-6" data-id="element-10455" />
                <span className="font-bold" data-id="element-10456">
                  Deze tekst had geen fouten — je hoefde niks te markeren.
                </span>
              </>}
          </motion.div>}
      </AnimatePresence>
    </div>;
}